import React from 'react';

/**
 * Landing-system brand mark.
 *
 * Renders the rounded-square glyph + brand name combo used in every
 * landing navbar and footer (Kloutsale, Kloutforce — and Katalag next).
 * Tone-aware so the same primitive works on light navbars (Kloutsale)
 * and dark navbars/footers (Kloutforce, Katalag).
 *
 * Caller is responsible for wrapping in <Link to="/"> when needed —
 * we don't assume a router here so the primitive stays composable.
 */
export type LandingBrandMarkTone = 'dark' | 'light';
export type LandingBrandMarkSize = 'sm' | 'md';

type Props = {
  /** Single character / short glyph (e.g. 'K'). Used when no logoSrc is provided. */
  glyph?: React.ReactNode;
  /**
   * Optional logo image. When provided, renders a circular logo container
   * instead of the gradient glyph box. Premium-feel: balanced padding,
   * object-contain, never cropped.
   */
  logoSrc?: string;
  /** Alt text for logoSrc. Falls back to `name`. */
  logoAlt?: string;
  /** Brand name shown next to the glyph. */
  name: string;
  tone?: LandingBrandMarkTone;
  size?: LandingBrandMarkSize;
  className?: string;
};

const sizeGlyph: Record<LandingBrandMarkSize, string> = {
  sm: 'h-9 w-9 rounded-xl text-base',
  md: 'h-10 w-10 rounded-2xl text-lg',
};

const sizeLogo: Record<LandingBrandMarkSize, string> = {
  sm: 'h-9 w-9 p-1.5',
  md: 'h-11 w-11 p-2',
};

const sizeName: Record<LandingBrandMarkSize, string> = {
  sm: 'text-lg',
  md: 'text-xl',
};

export default function LandingBrandMark({
  glyph,
  logoSrc,
  logoAlt,
  name,
  tone = 'dark',
  size = 'md',
  className = '',
}: Props) {
  const nameColor = tone === 'dark' ? 'text-white' : 'text-slate-950';
  const ringColor = tone === 'dark' ? 'border-white/15 bg-white/[0.04]' : 'border-slate-900/10 bg-slate-900/[0.03]';
  return (
    <span className={`flex items-center gap-3 no-underline ${className}`.trim()}>
      {logoSrc ? (
        <span
          className={`flex items-center justify-center rounded-full border ${ringColor} ${sizeLogo[size]} shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)]`}
        >
          <img
            src={logoSrc}
            alt={logoAlt ?? name}
            className="h-full w-full object-contain"
          />
        </span>
      ) : (
        <span
          className={`flex items-center justify-center bg-[hsl(var(--landing-accent))] font-bold text-white shadow-lg shadow-[hsl(var(--landing-accent)/0.3)] ${sizeGlyph[size]}`}
        >
          {glyph}
        </span>
      )}
      <span className={`font-semibold tracking-tight ${nameColor} ${sizeName[size]}`}>
        {name}
      </span>
    </span>
  );
}
